import React, { useEffect, useState } from 'react';
import styled from "styled-components";
import { connect } from "react-redux";
import { FormSubHeading } from '../../../../styles/form-styles.js';
import ABButton from '../../../../components/ABButton.js';
import DatePicker from 'react-datepicker';
import colors from '../../../../styles/colors.js';
import { EMCStat } from '../stats-types.js';
import { ToastContainer, toast } from 'react-toastify';
import { history } from '../../../../routers/AppRouter.js';
import ABInputField from '../../../../components/ABInputField';
import { useParams } from 'react-router-dom';
import { getClients } from '../../../../actions/clients.js';
import { Client } from '../../../../types/types.js';
import { createEMCStat, getEMCStat, updateEMCStat } from '../../../../actions/portal/emc-stat.js';
import ABDropzone from '../../../../components/ABDropzone';

const PageWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const HeadingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledHeading = styled.div`
  padding: 10px 10px 10px 0;
  font-size: 40px;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 0;
  div:first-child {
    padding-right: 10px;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
`;

const TextButton = styled.div`
  cursor: pointer;
  margin-right: 5px;
  color: ${colors.blue};
`;

const DatePickerStyle = styled.div`
  input {
    padding: 10px;
    font-size: 16px;
    width: 100%;
    min-width: 400px;
    margin-bottom: 20px;
    line-height: 25px;
  }
`;

const StyledSelect = styled.select`
  font-size: 16px;
  padding: 9px 10px 10px 10px;
  width: 450px;
  border: 1px solid ${colors.lightGray};
`;

const DefaultImagePreviewStyle = styled.div`
  img {
    width: 100px;
    height: 100px;
  }
`;

interface EMCStatsProps {
    createEMCStat: (emcStat: EMCStat) => void;
    updateEMCStat: (emcStat: EMCStat, emcStatId: string) => void;
    getEMCStat: (emcStatId: string) => void;
    success?: string | null;
    error?: string | null;
    emcStat: EMCStat;
    getClients: () => void;
    clients: Client[];
}

interface RouteParams {
    emcStatId: string;
}

function EmailStatsForm({ createEMCStat, success, error, emcStat, updateEMCStat, getClients, clients }: EMCStatsProps) {
    const { emcStatId } = useParams<RouteParams>();

    const [clientId, setClientId] = useState("");
    const [sent, setSent] = useState('');
    const [newsletterLink, setNewsletterLink] = useState('');
    const [delivered, setDelivered] = useState('');
    const [bounceRate, setBounceRate] = useState('');
    const [openRate, setOpenRate] = useState('');
    const [opens, setOpens] = useState('');
    const [clicks, setClicks] = useState('');
    const [ctr, setCtr] = useState('');
    const [ctor, setCtor] = useState('');
    const [deliveryRate, setDeliveryRate] = useState('');
    const [unsubscribeRate, setUnsubscribeRate] = useState('');
    const [spamRate, setSpamRate] = useState('');
    const [growthRate, setGrowthRate] = useState('');
    const [date, setDate] = useState<Date | null>(null);
    const [subject, setSubject] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [imageUploaded, setImageUploaded] = useState(false);

    useEffect(() => {
        getClients();
    }, []);

    useEffect(() => {
        if (success) {
            if (!emcStatId) {
                toast.success("EMC stat created successfully!");
                history.push('./');
            } else {
                toast.success("EMC stat updated successfully!");
                history.push('../');
            }
        }
        if (error) {
            toast.error('Error Creating Stat');
        }
    }, [success, error]);

    useEffect(() => {
        console.log('emcStatId: ', emcStatId);
        if (emcStatId) {
            getEMCStat(emcStatId);
        } else {
            clearForm();
        }
    }, [emcStatId]);

    useEffect(() => {
        if (emcStat && emcStatId && Object.keys(emcStat).length !== 0) {
            setClientId(emcStat.clientId?.toString() || '');
            setImageUrl(emcStat.imageUrl?.toString() || '');
            setSent(emcStat.sent?.toString() || '');
            setNewsletterLink(emcStat.newsletterLink?.toString() || '');
            setDelivered(emcStat.delivered?.toString() || '');
            setBounceRate(emcStat.bounceRate?.toString() || '');
            setOpenRate(emcStat.openRate?.toString() || '');
            setOpens(emcStat.opens?.toString() || '');
            setClicks(emcStat.clicks?.toString() || '');
            setCtr(emcStat.ctr?.toString() || '');
            setCtor(emcStat.ctor?.toString() || '');
            setDeliveryRate(emcStat.deliveryRate?.toString() || '');
            setUnsubscribeRate(emcStat.unsubscribeRate?.toString() || '');
            setSpamRate(emcStat.spamRate?.toString() || '');
            setGrowthRate(emcStat.growthRate?.toString() || '');
            setSubject(emcStat.subject?.toString() || '');
            setDate(emcStat.date ? new Date(emcStat.date) : null);
        }
    }, [emcStat]);

    const clearForm = () => {
        setSent('');
        setNewsletterLink('');
        setDelivered('');
        setBounceRate('');
        setOpenRate('');
        setOpens('');
        setClicks('');
        setCtr('');
        setCtor('');
        setDeliveryRate('');
        setUnsubscribeRate('');
        setSpamRate('');
        setGrowthRate('');
        setDate(null);
        setSubject('');
        setClientId('');
        setImageUrl('');
    };

    const onCreate = () => {

        if (imageUploaded && !imageUrl) {
            toast.error('Error Uploading Image');
            return;
        }

        if (clientId && sent && delivered && openRate && opens && clicks && ctr && ctor && bounceRate && newsletterLink && subject && date) {
            const emcStats: EMCStat = {
                sent: parseInt(sent, 10) || 0,
                newsletterLink,
                delivered: parseInt(delivered, 10) || 0,
                openRate: parseFloat(openRate) || 0,
                opens: parseInt(opens, 10) || 0,
                clicks: parseInt(clicks, 10) || 0,
                ctr: parseFloat(ctr) || 0,
                ctor: parseFloat(ctor) || 0,
                bounceRate: parseFloat(bounceRate) || 0,
                deliveryRate: deliveryRate ? parseFloat(deliveryRate) : undefined,
                unsubscribeRate: unsubscribeRate ? parseFloat(unsubscribeRate) : undefined,
                spamRate: spamRate ? parseFloat(spamRate) : undefined,
                growthRate: growthRate ? parseFloat(growthRate) : undefined,
                date: date.toISOString(),
                subject,
                clientId: parseInt(clientId, 10),
                imageUrl
            };
            if (emcStatId) {
                updateEMCStat(emcStats, emcStatId);
            } else {
                createEMCStat(emcStats);
            }
        } else {
            toast.error('Please fill out all required fields.');
        }
    };

    const onCancel = () => {
        clearForm();
        if (!emcStatId) {
            history.push('./');
        } else {
            history.push(`../${emcStatId}`);
        }
    }

    const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) =>
        (e: React.ChangeEvent<HTMLInputElement>) => setter(e.currentTarget.value);

    const handleSelectChange =
        (setter: React.Dispatch<React.SetStateAction<string>>) =>
            (e: React.ChangeEvent<HTMLSelectElement>) =>
                setter(e.target.value);

    const handleFilesDrop = (cloudfrontUrl: string) => {
        setImageUrl(cloudfrontUrl);
        setImageUploaded(true);
    };

    return (
        <PageWrapper>
            <HeadingContainer>
                <StyledHeading>{emcStatId ? "Edit" : "Create"} Email Marketing Stats</StyledHeading>
            </HeadingContainer>

            <FormSubHeading>Client ID:</FormSubHeading>
            {
                (clients && clients.length > 0) &&
                <StyledSelect
                    name="clientId"
                    value={clientId}
                    onChange={handleSelectChange(setClientId)}
                    placeholder="Enter Client Id"
                >
                    <option value="">Select</option>
                    {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                            {client.company} - {client.id}
                        </option>
                    ))}
                </StyledSelect>
            }

            <FormSubHeading>Appearance Date:</FormSubHeading>
            <DatePickerStyle>
                <DatePicker
                    selected={date}
                    onChange={setDate}
                    dateFormat="MMMM d, yyyy"
                    placeholderText="Select a date"
                />
            </DatePickerStyle>

            <FormSubHeading>Image Upload:</FormSubHeading>
            <ABDropzone onFilesDrop={handleFilesDrop} maxWidth={600} />

            {
                (imageUrl && !imageUploaded) &&
                <DefaultImagePreviewStyle>
                    <FormSubHeading>Image Preview:</FormSubHeading>
                    <img src={`https://${imageUrl}`} />
                </DefaultImagePreviewStyle>
            }

            <FormSubHeading>Email Marketing Link:</FormSubHeading>
            <ABInputField value={newsletterLink} onChange={handleInputChange(setNewsletterLink)} placeholder="https://..." />

            <FormSubHeading>Subject Line:</FormSubHeading>
            <ABInputField value={subject} onChange={handleInputChange(setSubject)} placeholder="Subject line" />

            <FormSubHeading>Sent:</FormSubHeading>
            <ABInputField value={sent} onChange={handleInputChange(setSent)} placeholder="Sent" />

            <FormSubHeading>Delivered:</FormSubHeading>
            <ABInputField value={delivered} onChange={handleInputChange(setDelivered)} placeholder="Delivered" />

            <FormSubHeading>Bounce Rate:</FormSubHeading>
            <ABInputField value={bounceRate} onChange={handleInputChange(setBounceRate)} placeholder="Bounce Rate" />

            <FormSubHeading>Open Rate:</FormSubHeading>
            <ABInputField value={openRate} onChange={handleInputChange(setOpenRate)} placeholder="Open Rate" />

            <FormSubHeading>Opens:</FormSubHeading>
            <ABInputField value={opens} onChange={handleInputChange(setOpens)} placeholder="Opens" />

            <FormSubHeading>CTR:</FormSubHeading>
            <ABInputField value={ctr} onChange={handleInputChange(setCtr)} placeholder="CTR" />

            <FormSubHeading>Clicks:</FormSubHeading>
            <ABInputField value={clicks} onChange={handleInputChange(setClicks)} placeholder="Clicks" />

            <FormSubHeading>CTOR:</FormSubHeading>
            <ABInputField value={ctor} onChange={handleInputChange(setCtor)} placeholder="CTOR" />

            <ActionButtons>
                <FooterButtons>
                    <ABButton
                        title={emcStatId ? "Update Stat" : "Create Stat"}
                        onClick={onCreate}
                    />
                    <TextButton title="cancel" onClick={onCancel}>Cancel</TextButton>
                </FooterButtons>
            </ActionButtons>
            <ToastContainer />
        </PageWrapper>
    );
};

const mapStateToProps = (state: any) => ({
    error: state.emcStats.error,
    success: state.emcStats.success,
    emcStat: state.emcStats.emcStat,
    clients: state.clients.data,
});

const mapDispatchToProps = (dispatch: any) => ({
    getClients: () => dispatch(getClients()),
    createEMCStat: (queryParams?: object) => dispatch(createEMCStat(queryParams)),
    updateEMCStat: (queryParams: object, emcStatId: string) => dispatch(updateEMCStat(queryParams, emcStatId)),
    getEMCStat: (emcStatId: string) => dispatch(getEMCStat(emcStatId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmailStatsForm);
